import { useTranslation } from 'react-i18next'
import { startOfDay } from 'date-fns'
import { useFormikContext } from 'formik'

import { useDateLimits, useScreen } from '../../../../../hooks'
import { DatePicker, DateTimePicker } from '../../../../DateTime'
import { FormikField } from '../../../../Formik'
import { SwitchInput } from '../../../../Switch'
import { APPOINTMENT_FORM_FIELD_NAME } from '../../AppointmentFormModal.constants'
import {
  getAppointmentFormDateErrorProps,
  getAppointmentFormEnd,
  getAppointmentFormSpanErrorKey,
} from '../../AppointmentFormModal.utils'
import { AppointmentFormModalHintedControl } from '../AppointmentFormModalHintedControl'

function AppointmentFormModalTimeFields() {
  const { t } = useTranslation()

  const { isMobileScreenSize } = useScreen()

  const { maxDate, minDate } = useDateLimits()

  const { setFieldValue, touched, values } = useFormikContext()

  const spanErrorKey = getAppointmentFormSpanErrorKey(values)

  const dateFieldProps = {
    borderless: !isMobileScreenSize,
    maxValue: maxDate,
    minValue: minDate,
  }

  function handleChangeFirstDay(firstDay) {
    if (!touched[APPOINTMENT_FORM_FIELD_NAME.lastDay]) {
      setFieldValue(APPOINTMENT_FORM_FIELD_NAME.lastDay, firstDay)
    }
  }

  function handleChangeAllDay(isAllDay) {
    const start = values[APPOINTMENT_FORM_FIELD_NAME.start]

    if (isAllDay && start && !touched[APPOINTMENT_FORM_FIELD_NAME.firstDay]) {
      const firstDay = startOfDay(start)

      setFieldValue(APPOINTMENT_FORM_FIELD_NAME.firstDay, firstDay)

      handleChangeFirstDay(firstDay)
    }
  }

  function handleChangeStart(start) {
    if (!touched[APPOINTMENT_FORM_FIELD_NAME.end]) {
      setFieldValue(
        APPOINTMENT_FORM_FIELD_NAME.end,
        getAppointmentFormEnd(start)
      )
    }
  }

  return (
    <>
      <FormikField
        Component={SwitchInput}
        fieldComponentProps={{
          label: `${t('Appointment.FormAllDayLabelKey')}:`,
          shouldAdaptToMobileScreen: true,
        }}
        name={APPOINTMENT_FORM_FIELD_NAME.isAllDay}
        onChange={handleChangeAllDay}
      />
      {values[APPOINTMENT_FORM_FIELD_NAME.isAllDay] ? (
        <>
          <FormikField
            {...dateFieldProps}
            Component={DatePicker}
            fieldComponentProps={{
              label: `${t('Appointment.FormStartLabelKey')}:`,
              required: true,
              shouldAdaptToMobileScreen: true,
            }}
            name={APPOINTMENT_FORM_FIELD_NAME.firstDay}
            placeholder={t('Appointment.FormDatePlaceholderKey')}
            onChange={handleChangeFirstDay}
          />
          <FormikField
            {...dateFieldProps}
            isHintError
            Component={AppointmentFormModalHintedControl}
            Control={DatePicker}
            fieldComponentProps={{
              label: `${t('Appointment.FormEndLabelKey')}:`,
              required: true,
              shouldAdaptToMobileScreen: true,
            }}
            getErrorProps={getAppointmentFormDateErrorProps(spanErrorKey)}
            hint={spanErrorKey ? t(spanErrorKey) : null}
            name={APPOINTMENT_FORM_FIELD_NAME.lastDay}
            placeholder={t('Appointment.FormDatePlaceholderKey')}
          />
        </>
      ) : (
        <>
          <FormikField
            {...dateFieldProps}
            Component={DateTimePicker}
            dateProps={{ placeholder: t('Appointment.FormDatePlaceholderKey') }}
            fieldComponentProps={{
              label: `${t('Appointment.FormStartLabelKey')}:`,
              required: true,
              shouldAdaptToMobileScreen: true,
            }}
            name={APPOINTMENT_FORM_FIELD_NAME.start}
            timeProps={{ placeholder: t('Appointment.FormTimePlaceholderKey') }}
            onChange={handleChangeStart}
          />
          <FormikField
            {...dateFieldProps}
            Component={DateTimePicker}
            dateProps={{ placeholder: t('Appointment.FormDatePlaceholderKey') }}
            errorText={spanErrorKey ? t(spanErrorKey) : undefined}
            fieldComponentProps={{
              label: `${t('Appointment.FormEndLabelKey')}:`,
              required: true,
              shouldAdaptToMobileScreen: true,
            }}
            name={APPOINTMENT_FORM_FIELD_NAME.end}
            timeProps={{ placeholder: t('Appointment.FormTimePlaceholderKey') }}
          />
        </>
      )}
    </>
  )
}

export default AppointmentFormModalTimeFields
